import { z } from 'zod';
import { accountFilterSchema, idArray, subscriberTypeSchema } from './shared.schema';

export const validationAndOtherDetailRateTypeSchema = z.enum([
	'Validation',
	'Hotel Storage',
	'Office/Retail Rent',
	'Miscellaneous',
]);

export const validationAndOtherDetailRateTypes = validationAndOtherDetailRateTypeSchema.options;

export type ValidationAndOtherDetailRateType = z.infer<typeof validationAndOtherDetailRateTypeSchema>;

export const validationAndOtherDetailDefaultRateTypes = [
	'Validation',
	'Hotel Storage',
	'Office/Retail Rent',
	'Miscellaneous',
] as const satisfies readonly ValidationAndOtherDetailRateType[];

export const validationAndOtherDetailReportFormatSchema = z.enum(['csv', 'pdf']);

export const validationAndOtherDetailParametersSchema = z
	.object({
		accountFilter: accountFilterSchema.optional(),
		subscriberType: subscriberTypeSchema.optional(),
		rateTypes: z.array(validationAndOtherDetailRateTypeSchema).min(1),
		invoiceStartDate: z.iso.date().optional(),
		invoiceEndDate: z.iso.date().optional(),
		parkingPeriodStartDate: z.iso.date().optional(),
		parkingPeriodEndDate: z.iso.date().optional(),
		areaIds: idArray.optional(),
		branchIds: idArray.optional(),
		districtIds: idArray.optional(),
		facilityIds: z.array(z.string()).optional(),
		format: validationAndOtherDetailReportFormatSchema.default('csv'),
		facilityNumber: z.union([z.string(), z.number()]).optional(),
	})
	.superRefine((value, ctx) => {
		const hasInvoiceStartDate = value.invoiceStartDate !== undefined;
		const hasParkingPeriodStart = value.parkingPeriodStartDate !== undefined;

		if (!hasInvoiceStartDate && !hasParkingPeriodStart) {
			ctx.addIssue({
				code: 'custom',
				path: ['invoiceStartDate'],
				message: 'Either invoiceStartDate or parkingPeriodStartDate is required',
			});
		}

		if (value.invoiceEndDate && !hasInvoiceStartDate) {
			ctx.addIssue({
				code: 'custom',
				path: ['invoiceEndDate'],
				message: 'Cannot specify invoiceEndDate without invoiceStartDate',
			});
		}

		if (value.parkingPeriodEndDate && !hasParkingPeriodStart) {
			ctx.addIssue({
				code: 'custom',
				path: ['parkingPeriodEndDate'],
				message: 'Cannot specify parkingPeriodEndDate without parkingPeriodStartDate',
			});
		}

		if (
			value.parkingPeriodStartDate &&
			value.parkingPeriodEndDate &&
			value.parkingPeriodEndDate < value.parkingPeriodStartDate
		) {
			ctx.addIssue({
				code: 'custom',
				path: ['parkingPeriodEndDate'],
				message: 'Parking period end must be on or after parking period start',
			});
		}
	});

export type ValidationAndOtherDetailParameters = z.infer<typeof validationAndOtherDetailParametersSchema>;
export type ValidationAndOtherDetailReportFormat = z.infer<typeof validationAndOtherDetailReportFormatSchema>;
